import React from "react";
import { Link } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "~/components/ui/card";
import { Button } from "~/components/ui/button";
import { Testimonials } from "~/components/ui/testimonials";
import { BookPlus, HelpCircle, Star } from "lucide-react";
import { ProfileSectionHeader } from "~/components/ui/profile-section-header";
import { MarkdownContent } from "~/components/markdown-content";
import { OfferTag } from "~/components/ui/offer-tag";
import { ImageSparkle } from "~/components/ui/image-sparkle";
import { SatisfactionShield } from "~/components/ui/satisfaction-shield";
import { Accordion } from "~/components/ui/accordion";
import { ArrowRight } from "~/components/ui/arrow-right";
import { ArrowLeft } from "~/components/ui/arrow-left";
import { VideoPlayer } from "~/components/ui/video-player";
import { CDNImage } from "~/components/ui/cdn-image";
import { PriceDisplay } from "~/components/ui/price-display";

import {
  createCoachMeta,
  createServiceStructuredData,
} from "~/lib/shared/meta";
import { offerQueryOptions } from "~/lib/query-options";
import { shouldShowBookingCTAs, getCoachCTAText } from "~/lib/coach-cta-utils";
import { useOfferCheckout } from "~/hooks/use-offer-checkout";

export const Route = createFileRoute("/$username/_profile/offer/$offerId/")({
  loader: async ({ context, params }) => {
    const queryOptions = offerQueryOptions.byUsernameAndId(
      params.username,
      params.offerId,
    );
    return await context.queryClient.ensureQueryData(queryOptions);
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        title: "Coaching Offer | Dream Coach Match",
        meta: [
          {
            name: "description",
            content: "View this coaching offer",
          },
        ],
      };
    }

    const { coach, offer } = loaderData;
    const coachName = coach?.fullName || params.username;
    const offerTitle = offer?.headline || "Coaching Offer";
    const description =
      offer?.description ||
      `Discover ${coachName}'s coaching offer designed to help you achieve your goals.`;
    const imageUrl =
      offer?.vslVideoUrl ||
      coach?.profile?.heroImageSrc ||
      coach?.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/offer/${params.offerId}`;

    const metaTags = createCoachMeta(coachName, {
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "product",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      canonical: currentUrl,
      author: coachName,
      keywords: [
        "coaching",
        "offer",
        "service",
        offer?.tags?.map((t) => t.title).join(", ") || "",
        coach?.profile?.coachingTitle || "life coach",
      ].filter(Boolean),
    });

    // Add structured data for the service
    const structuredData = createServiceStructuredData({
      name: offerTitle,
      description,
      provider: coachName,
      url: currentUrl,
      price: offer?.price ? (offer.price / 100).toString() : undefined,
      image: imageUrl || undefined,
    });

    return {
      ...metaTags,
      title: `${offerTitle} - ${coachName} | Dream Coach Match`,
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: OfferPage,
});

function OfferPage() {
  const data = Route.useLoaderData();
  const { createCheckout } = useOfferCheckout();

  const { coach, offer } = data;

  // Check for success parameter
  const urlParams = new URLSearchParams(window.location.search);
  const showSuccess = "true" === urlParams.get("success");

  // Function to scroll to bottom CTA
  const scrollToPrice = () => {
    document
      .getElementById("price-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCheckout = (e: React.FormEvent) => {
    e.preventDefault();
    createCheckout.mutate(offer.id);
  };

  return (
    <div className="flex-basis-0 flex-grow-6 flex flex-col items-stretch gap-8">
      {showSuccess && (
        <div className="rounded-lg bg-green-50 p-4 text-green-700">
          <p className="font-medium">Thank you for your purchase!</p>
          <p className="text-sm">
            You will receive a confirmation email shortly.
          </p>
        </div>
      )}
      {/* Show error message if checkout creation failed */}
      {createCheckout.error && (
        <div className="rounded-lg bg-red-50 p-4 text-red-700">
          <p className="font-medium">Failed to create checkout session</p>
          <p className="text-sm">{createCheckout.error.message}</p>
        </div>
      )}
      {/* Introduction */}
      <Card>
        <CardContent>
          {offer.vslVideoUrl && (
            <VideoPlayer
              src={offer.vslVideoUrl}
              title={offer.headline ?? ""}
              className="mb-6 aspect-video w-full rounded-lg"
            />
          )}
          {offer.preHeadline && (
            <ProfileSectionHeader text={offer.preHeadline} />
          )}
          {offer.headline && (
            <h1 className="font-heading text-3xl">{offer.headline}</h1>
          )}
          {offer.description && (
            <div className="text-text-secondary mt-4 border-l-2 border-l-orange-200 py-1 pl-2">
              <MarkdownContent content={offer.description} />
            </div>
          )}
          <div className="mt-6 w-full text-center">
            {offer.directSaleEnabled ? (
              <Button variant="fancy" size="lg" onClick={scrollToPrice}>
                See pricing
              </Button>
            ) : (
              shouldShowBookingCTAs(coach) && (
                <Link
                  to={`/$username/book`}
                  params={{ username: coach.username }}
                >
                  <Button variant="fancy" size="lg">
                    {getCoachCTAText(coach)}
                  </Button>
                </Link>
              )
            )}
          </div>
        </CardContent>
      </Card>
      {/* Offer Tags */}
      {0 < offer.tags.length && (
        <Card>
          <CardContent>
            <div className="flex flex-wrap items-center justify-around gap-4">
              {offer.tags.map((tag) => (
                <OfferTag key={tag.id} tag={tag} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Struggles */}
      {(offer.strugglesHeadline || offer.strugglesDescription) && (
        <Card className="z-20">
          <CardContent>
            <div className="flex flex-col gap-4 md:flex-row">
              {offer.strugglesImageUrl && (
                <div className="relative md:w-1/3">
                  <CDNImage
                    src={offer.strugglesImageUrl}
                    alt={offer.strugglesHeadline ?? ""}
                    className="aspect-4/5 w-full rounded-lg object-cover"
                    width={384}
                    height={480}
                  />
                  <ImageSparkle />
                </div>
              )}
              <div className="flex flex-col gap-4 md:w-2/3">
                {offer.strugglesHeadline && (
                  <div className="flex flex-col">
                    <ProfileSectionHeader text="Problems we solve" />
                    <h2 className="font-heading text-3xl">
                      {offer.strugglesHeadline}
                    </h2>
                  </div>
                )}
                {offer.strugglesDescription && (
                  <MarkdownContent
                    className="text-text-secondary border-l-2 border-l-orange-200 py-1 pl-2"
                    content={offer.strugglesDescription}
                  />
                )}
                {offer.directSaleEnabled ? (
                  <Button variant="fancy" onClick={scrollToPrice}>
                    See pricing
                  </Button>
                ) : (
                  shouldShowBookingCTAs(coach) && (
                    <Link
                      to={`/$username/book`}
                      params={{ username: coach.username }}
                    >
                      <Button variant="fancy">{getCoachCTAText(coach)}</Button>
                    </Link>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Results Section */}
      {offer.resultsHeadline && (
        <div className="relative z-10">
          <ArrowRight className="absolute -right-24 -top-64 w-32" />
          <Card className="bg-brand-50 border-brand-200">
            <CardContent className="flex flex-col gap-4 md:flex-row">
              <div className="flex flex-col gap-4">
                {offer.resultsPreHeadline && (
                  <ProfileSectionHeader
                    text={offer.resultsPreHeadline}
                    color="brand"
                  />
                )}
                <h2 className="font-heading text-2xl leading-none">
                  {offer.resultsHeadline}
                </h2>
                <MarkdownContent
                  content={offer.resultsDescription}
                  className="text-text-secondary border-l-brand-700 border-l-2 py-2 pl-2"
                />
                {offer.directSaleEnabled ? (
                  <Button variant="fancy" onClick={scrollToPrice}>
                    See pricing
                  </Button>
                ) : (
                  shouldShowBookingCTAs(coach) && (
                    <Link
                      to={`/$username/book`}
                      params={{ username: coach.username }}
                    >
                      <Button variant="fancy">{getCoachCTAText(coach)}</Button>
                    </Link>
                  )
                )}
              </div>
              {offer.resultsImageUrl && (
                <div className="md:w-1/2">
                  <CDNImage
                    src={offer.resultsImageUrl}
                    alt={offer.resultsHeadline}
                    className="aspect-4/5 w-full rounded-lg object-cover"
                    width={384}
                    height={480}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* About the Program */}
      {offer.programHeadline && (
        <div className="relative z-0">
          <ArrowLeft className="absolute -left-28 -top-60 w-32" />
          <Card>
            <CardContent className="flex flex-col gap-4 md:flex-row">
              <div className="grow-1 flex basis-0 flex-col gap-4">
                <ProfileSectionHeader text="About the Package" />
                <h2 className="font-heading text-2xl">
                  {offer.programHeadline}
                </h2>
                <MarkdownContent
                  className="text-text-secondary border-l-2 border-l-orange-200 py-1 pl-2"
                  content={offer.programDescription}
                />
                {offer.programImageUrl && (
                  <div className="rounded-lg border border-gray-100 bg-white p-4">
                    <CDNImage
                      src={offer.programImageUrl}
                      alt={offer.programHeadline}
                      className="w-full rounded object-cover"
                      width={384}
                      height={480}
                    />
                  </div>
                )}
                <div className="flex gap-4">
                  {offer.applicationDeadline && (
                    <div className="border-gradient-pro-purple grow-1 basis-0 rounded-[12px] p-[2px]">
                      <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[10px] bg-white p-2 text-center">
                        <span className="text-text-tertiary text-sm">
                          Application Deadline
                        </span>
                        <span className="font-heading text-brand-700 text-xl">
                          {new Intl.DateTimeFormat(undefined, {
                            dateStyle: "medium",
                          }).format(new Date(offer.applicationDeadline))}
                        </span>
                      </div>
                    </div>
                  )}
                  {offer.cohortStartDate && (
                    <div className="border-gradient-pro-purple grow-1 basis-0 rounded-[12px] p-[2px]">
                      <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[10px] bg-white p-2 text-center">
                        <span className="text-text-tertiary text-sm">
                          Cohort Starts
                        </span>
                        <span className="font-heading text-brand-700 text-xl">
                          {new Intl.DateTimeFormat(undefined, {
                            dateStyle: "medium",
                          }).format(new Date(offer.cohortStartDate))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="grow-1 flex basis-0 flex-col gap-4">
                {offer.components.map((component) => (
                  <div
                    key={component.id}
                    className="rounded-lg border border-gray-100 bg-white p-4"
                  >
                    {component.preHeader && (
                      <ProfileSectionHeader text={component.preHeader} />
                    )}
                    {component.imageUrl && (
                      <CDNImage
                        src={component.imageUrl}
                        alt={component.title}
                        className="mb-4 w-full rounded object-cover"
                        width={384}
                        height={123}
                      />
                    )}
                    <h3 className="font-heading mb-2 text-lg">
                      {component.title}
                    </h3>
                    <MarkdownContent
                      className="text-text-secondary prose:leading-snug text-sm"
                      content={component.content}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Benefits */}
      {!!(0 < offer.benefits.length) && (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <h3 className="font-heading flex items-center gap-2">
              <Star className="text-brand-700 size-[1em]" /> Benefits
            </h3>
            {offer.benefits.map((benefit, index) => (
              <div
                key={benefit.id}
                className={`flex flex-col rounded-lg border border-gray-100 bg-white p-4 ${
                  0 === index % 2 ? "md:flex-row" : "md:flex-row-reverse"
                } gap-4`}
              >
                <div className="md:w-1/3">
                  {benefit.imageSrc && (
                    <CDNImage
                      src={benefit.imageSrc}
                      alt={benefit.title}
                      width={300}
                      height={123}
                      className="w-full rounded-lg object-cover"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-4 md:w-2/3">
                  <h3 className="font-heading flex items-center text-xl font-medium">
                    {benefit.title}
                  </h3>
                  <MarkdownContent
                    content={benefit.content}
                    className="text-text-secondary border-l-2 border-orange-200 pl-2"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {/* Testimonials */}
      <Testimonials testimonials={coach.testimonials} />
      {/* Bonuses */}
      {!!(0 < offer.bonuses.length) && (
        <Card>
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <BookPlus className="text-brand-700 size-[1em]" />
              <h2 className="font-heading text-lg">Bonuses</h2>
            </div>
            {offer.bonuses.map((bonus) => (
              <div
                key={bonus.id}
                className="flex gap-4 rounded-lg border border-gray-200 bg-white p-4 md:flex-row"
              >
                <div className="grow-1 basis-0">
                  <h3 className="font-heading text-xl font-medium">
                    {bonus.title}
                  </h3>
                  <MarkdownContent
                    className="text-text-secondary mt-4 border-l-2 border-l-orange-200 pl-2"
                    content={bonus.content}
                  />
                </div>
                {bonus.imageSrc && (
                  <div className="md:w-1/3">
                    <CDNImage
                      src={bonus.imageSrc}
                      alt={bonus.title}
                      width={300}
                      height={200}
                      className="aspect-3/2 w-full rounded object-cover"
                    />
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {/* Satisfaction Guaranteed */}
      {offer.satisfactionGuaranteeText && (
        <Card className="from-brand-50 to-brand-100 via-brand-200 border-brand-200 border bg-gradient-to-br">
          <CardContent className="flex items-center gap-4 md:flex-row">
            <SatisfactionShield />
            <div className="flex flex-col">
              <h2 className="font-heading mb-1 text-xl">
                Satisfaction Guaranteed
              </h2>
              <MarkdownContent
                content={offer.satisfactionGuaranteeText}
                className="text-text-secondary mb-4"
              />
              {offer.directSaleEnabled ? (
                <Button onClick={scrollToPrice}>See pricing</Button>
              ) : (
                shouldShowBookingCTAs(coach) && (
                  <Link
                    to={`/$username/book`}
                    params={{ username: coach.username }}
                  >
                    <Button>Get Started</Button>
                  </Link>
                )
              )}
            </div>
          </CardContent>
        </Card>
      )}
      {/* FAQ */}
      {!!(0 < offer.faqs.length) && (
        <Card>
          <CardContent>
            <div className="flex items-center gap-2">
              <HelpCircle className="text-brand-700 size-[1em]" />
              <h2 className="font-heading text-lg">
                Frequently Asked Questions
              </h2>
            </div>
            <Accordion.Root className="mt-2" type="single" collapsible>
              {offer.faqs.map((faq) => (
                <Accordion.Item key={faq.id} value={faq.question}>
                  <Accordion.Trigger>{faq.question}</Accordion.Trigger>
                  <Accordion.Content className="text-text-secondary">
                    <MarkdownContent content={faq.answer} />
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </CardContent>
        </Card>
      )}
      {/* Bottom CTA */}
      {(offer.bottomCtaHeadline || offer.bottomCtaText) && (
        <Card
          id="price-section"
          className="border-gray-400 bg-gradient-to-br from-purple-100 via-violet-100 to-purple-200"
        >
          <CardContent className="mx-auto max-w-prose p-8 py-12 text-center">
            {offer.bottomCtaHeadline && (
              <h2 className="font-heading text-2xl font-semibold">
                {offer.bottomCtaHeadline}
              </h2>
            )}
            {offer.bottomCtaText && (
              <p className="text-text-secondary mt-2 leading-normal">
                {offer.bottomCtaText}
              </p>
            )}
            {offer.directSaleEnabled ? (
              <>
                <div className="mt-6">
                  <PriceDisplay
                    price={offer.price!}
                    currency={offer.currency!}
                    originalPrice={offer.originalPrice}
                    size="lg"
                  />
                </div>
                <form onSubmit={handleCheckout}>
                  <input type="hidden" name="offerId" value={offer.id} />
                  <Button
                    type="submit"
                    variant="fancy"
                    size="lg"
                    className="mt-6 w-full"
                    disabled={createCheckout.isPending}
                  >
                    Buy Now
                  </Button>
                </form>
              </>
            ) : (
              shouldShowBookingCTAs(coach) && (
                <Link
                  to={`/$username/book`}
                  params={{ username: coach.username }}
                  className="mt-6 block w-full text-center"
                >
                  <Button variant="fancy" size="lg">
                    {getCoachCTAText(coach)}
                  </Button>
                </Link>
              )
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
